import { ReactElement, useEffect, useRef, useState } from "react";
import Chart from "../../Components/Charts/Base/Chart";
import { IInitResult, SciChartGroup } from "scichart-react";
import { ModifierGroup } from "../../../Shared/SciChart/ModifierGroup";
import { ChartModifierBase2D, ISciChartSurfaceBase, SciChartVerticalGroup } from "scichart";
import { synchronizeXVisibleRanges } from "../../../Shared/SciChart/ChartConfiguration";
import ChartWaitLoader from "../../../Shared/Components/ChartWaitLoader";
import useChartStore from "../../../../Zustand/chartStore";
import moment from "moment";
import { 
  GetVisibleRangeSingleSeries, 
  GetVisibleRangeMultipleSeries, 
  GetVisibleRangePressureSeries,
  GetVisibleRangeComplianceSeries,
  GetVisibleRangeDiagnosticSeries,
  GetVisibleRangeTimelineSeries
} from "../../../../Utilities/ChartUtilities";

export interface StatusChartWrapperConfig {
  // Chart component to render
  chartComponent: React.ComponentType<any>;
  
  // Props to pass to chart component (optional)
  chartComponentProps?: Record<string, any>;
  
  // Data for status and main chart
  statusChartData: any;
  chartData: any;
  
  // Configuration
  isSimpleChart?: boolean;
  chartParentHeight?: number;
  greasePumpData?: {
    serialNo: string;
    channelNo: number;
    timestamps: number[];
    values: number[];
  };
  
  // Modifier IDs for chart synchronization
  modifierIds: {
    modifierGroup: string; // e.g., "ActivityModifierGroup", "AssetModifierGroup", "DiagnosticsModifierGroup"
    rollOver: string; // e.g., "ActivityRollOver", "AssetRollOver", "DiagnosticsRollOver"
  };
  
  // Visible range calculation - pass a function that takes (visibleRangeRef, chartData, startDateUnix, endDateUnix)
  // and returns the visible range. The wrapper will extract dates from dateFilter automatically.
  calculateVisibleRange: (visibleRangeRef: any, chartData: any, startDateUnix: number, endDateUnix: number) => any;
  
  // Legend IDs
  legendIds: {
    chart: string; // e.g., "legend-activity-flow", "legend-diagnostics"
    status: string; // e.g., "legend-status"
  };
  
  // Chart key for React
  chartKey: string;
  
  // Additional props to pass to chart component
  chartProps?: Record<string, any>;
  
  // Layout options
  isLegendColumn?: boolean;
  showStatus?: boolean;
  isFullscreen?: boolean;
  
  // Optional: Custom legend modifier (for FlowRate)
  customLegendModifier?: (statusChart: any, chart: any) => void;
  
  // Optional: Custom key for SciChartGroup (for fullscreen support)
  sciChartGroupKey?: string;
  /** When provided (e.g. from Anomaly Detector dialog), controls legend visibility. When undefined, legend always shows. */
  showLegendOverride?: boolean;
}

export default function StatusChartWrapper(config: StatusChartWrapperConfig): ReactElement {
  const {
    chartComponent: ChartComponent,
    chartComponentProps = {},
    statusChartData,
    chartData,
    isSimpleChart,
    chartParentHeight,
    greasePumpData,
    modifierIds,
    calculateVisibleRange,
    legendIds,
    chartKey,
    chartProps = {},
    isLegendColumn,
    showStatus,
    isFullscreen,
    customLegendModifier,
    sciChartGroupKey,
    showLegendOverride
  } = config;

  const emptyVisibleRange = { min: undefined, max: undefined };
  const [modifierGroup] = useState(new ModifierGroup());
  const verticalGroup = new SciChartVerticalGroup();
  const [isChartsInitialised, setIsChartsInitialised] = useState(false);
  const visibleRangeRef = useRef(useChartStore.getState().visibleRange ?? emptyVisibleRange);

  useEffect(() => {
    const unsubscribe = useChartStore.subscribe((state) => {
      visibleRangeRef.current = state.visibleRange ?? emptyVisibleRange;
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate visible range using the provided function
  const dateFilter = chartData.dateFilter || statusChartData.dateFilter;
  const filterStartDateUnix = moment(dateFilter.startDate).unix();
  const filterEndDateUnix = moment(dateFilter.endDate).unix();
  
  // Call the calculateVisibleRange function
  const visibleRange = calculateVisibleRange(
    visibleRangeRef.current,
    chartData,
    filterStartDateUnix,
    filterEndDateUnix
  );

  // When the guard recomputes the window (the shared range was empty or didn't fit
  // the current filter), persist it so the other tabs adopt the SAME window instead
  // of each recomputing a different default from its own data. When the range is kept
  // (zoom still fits the filter), computed === stored and this is a no-op.
  useEffect(() => {
    if (visibleRange?.min == null || visibleRange?.max == null) return;
    const stored = useChartStore.getState().visibleRange;
    if (stored?.min !== visibleRange.min || stored?.max !== visibleRange.max) {
      useChartStore.getState().setVisibleRange(visibleRange.min, visibleRange.max);
    }
  }, [visibleRange?.min, visibleRange?.max]);

  const enhancedStatusChartData = {
    ...statusChartData,
    modifierGroup: modifierGroup,
    visibleRange
  };

  const enhancedChartData = {
    ...chartData,
    modifierGroup: modifierGroup,
    visibleRange
  };

  const configureStatusAndChartDataBindings = (initResults: IInitResult<ISciChartSurfaceBase>[] | [any, any]) => {
    const [statusChart, mainChart] = initResults;
    
    modifierGroup.clear();

    // Add status chart modifiers
    const statusRollOverModifier = statusChart.sciChartSurface.chartModifiers.getById("StatusRollOver");
    if (statusRollOverModifier) {
      modifierGroup.add(statusRollOverModifier as ChartModifierBase2D);
    }

    // Add main chart modifiers
    const chartRollOverModifier = mainChart.sciChartSurface.chartModifiers.getById(modifierIds.rollOver);

    if (chartRollOverModifier) {
      modifierGroup.add(chartRollOverModifier as ChartModifierBase2D);
    }

    // Synchronize visible ranges
    synchronizeXVisibleRanges([statusChart.sciChartSurface, mainChart.sciChartSurface], () => true);

    // Add to vertical group
    verticalGroup.addSurfaceToGroup(statusChart.sciChartSurface);
    verticalGroup.addSurfaceToGroup(mainChart.sciChartSurface);

    // Apply custom legend modifier if provided (e.g., for FlowRate)
    if (customLegendModifier) {
      customLegendModifier(statusChart, mainChart);
    }
  };

  const shouldShowStatusBar = showStatus !== false || (greasePumpData?.timestamps?.length ?? 0) > 0;

  const renderCharts = () => {
    const mainChartElement = (
      <ChartComponent
        key={chartKey}
        chartData={enhancedChartData}
        isSimpleChart={isSimpleChart}
        chartParentHeight={chartParentHeight}
        isFullscreen={isFullscreen || chartProps.isFullscreen}
        {...chartComponentProps}
        {...chartProps}
      />
    );

    const statusChartElement = shouldShowStatusBar ? (
      <Chart
        chartType="status"
        key={`status-chart-${chartKey}`}
        chartData={enhancedStatusChartData}
        isSimpleChart={isSimpleChart}
        greasePumpData={greasePumpData}
        showStatus={showStatus}
        isFullscreen={isFullscreen || chartProps.isFullscreen}
      />
    ) : null;

    // When isSimpleChart, the main chart uses position:absolute to fill its parent.
    // Wrap in a flex column so the status bar reserves space and the chart fills the rest.
    if (isSimpleChart && shouldShowStatusBar) {
      return (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flexShrink: 0 }}>
            {statusChartElement}
          </div>
          <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
            {mainChartElement}
          </div>
        </div>
      );
    }

    const legendStyle = showLegendOverride !== undefined ? { display: showLegendOverride ? undefined : 'none' } : undefined;

    if (isLegendColumn) {
      return (
        <div className="flex flex-col lg:flex-row items-start">
          <div className="w-full min-w-0 lg:flex-1">
            {statusChartElement}
            {mainChartElement}
          </div>
          <div className="w-full lg:w-auto mt-4 lg:mt-0 lg:ml-4 overflow-y-auto" style={legendStyle}>
            <div className="chart-legends-inline-flow legend-column">
              <div id={legendIds.chart} key="chart-legend-column" className="chart-legend-inline-group"></div>
              {showStatus !== false && <div id={legendIds.status} key="status-legend-column" className="chart-legend-inline-group"></div>}
            </div>
          </div>
        </div>
      );
    }

    if (!shouldShowStatusBar) {
      return mainChartElement;
    }

    const legendElement = (
      <div className="chart-legends-inline-flow" style={legendStyle}>
        <div id={legendIds.chart} key="chart-legend" className="chart-legend-inline-group"></div>
        {showStatus !== false && <div id={legendIds.status} key="status-legend" className="chart-legend-inline-group"></div>}
      </div>
    );


    return (
      <>
        {statusChartElement}
        {mainChartElement}
        {legendElement}
      </>
    );
  };

  return (
    <>
      <SciChartGroup
        key={sciChartGroupKey}
        onInit={(initResults: IInitResult[]) => {
          if (shouldShowStatusBar) {
            configureStatusAndChartDataBindings(initResults);
          }
          setIsChartsInitialised(true);
        }}
      >
        {isChartsInitialised ? null : <ChartWaitLoader />}
        {renderCharts()}
      </SciChartGroup>
    </>
  );
}

// Helper functions to adapt different GetVisibleRange* function signatures
export const createVisibleRangeCalculator = {
  singleSeries: (timestamps: number[]) => 
    (visibleRangeRef: any, chartData: any, startDateUnix: number, endDateUnix: number) =>
      GetVisibleRangeSingleSeries(visibleRangeRef, timestamps, startDateUnix, endDateUnix),
  
  multipleSeries: (dataCharts: any[]) =>
    (visibleRangeRef: any, chartData: any, startDateUnix: number, endDateUnix: number) =>
      GetVisibleRangeMultipleSeries(visibleRangeRef, dataCharts, startDateUnix, endDateUnix),
  
  pressure: (latestPressureTimestamp: number, latestVolumeTimestamp: number) =>
    (visibleRangeRef: any, chartData: any, startDateUnix: number, endDateUnix: number) =>
      GetVisibleRangePressureSeries(visibleRangeRef, latestPressureTimestamp, latestVolumeTimestamp, startDateUnix, endDateUnix),
  
  compliance: (latestVolumeTimestamp: number, latestUnplannedTimestamp: number) =>
    (visibleRangeRef: any, chartData: any, startDateUnix: number, endDateUnix: number) =>
      GetVisibleRangeComplianceSeries(visibleRangeRef, latestVolumeTimestamp, latestUnplannedTimestamp, startDateUnix, endDateUnix),
  
  diagnostics: (latestDiagnosticTimestamp: number, latestMetricTimestamp: number, lastDataPointTimestamp: number) =>
    (visibleRangeRef: any, chartData: any, startDateUnix: number, endDateUnix: number) =>
      GetVisibleRangeDiagnosticSeries(visibleRangeRef, latestDiagnosticTimestamp, latestMetricTimestamp, lastDataPointTimestamp, startDateUnix, endDateUnix),
  
  timeline: (seriesData: Array<{ timestamps: number[] }>) =>
    (visibleRangeRef: any, chartData: any, startDateUnix: number, endDateUnix: number) =>
      GetVisibleRangeTimelineSeries(visibleRangeRef, seriesData, startDateUnix, endDateUnix),
};
